<template>
  <!--
    v-show e NÃO v-if: o painel é montado uma única vez em App.vue e apenas se
    esconde. É o que mantém a conversa viva ao navegar entre telas — antes o
    chat vivia dentro do <Menu>, que App.vue remonta via `:key` sempre que os
    acessos mudam ou o token é renovado, e a conversa sumia no meio do uso.
  -->
  <aside
    v-show="isOpen"
    class="clara"
    :class="{ 'clara--mobile': isMobile, 'clara--resizing': isResizing }"
    :style="panelStyle"
    aria-label="Clara AI, assistente"
  >
    <!-- Alça de redimensionamento. Também responde a teclado: sem isso, quem
         não usa mouse não tem como ajustar a largura. -->
    <div
      v-if="!isMobile"
      class="clara__resizer"
      role="separator"
      aria-orientation="vertical"
      aria-label="Redimensionar a Clara"
      tabindex="0"
      @mousedown.prevent="startResize"
      @keydown.left.prevent="adjustWidth(24)"
      @keydown.right.prevent="adjustWidth(-24)"
    >
      <span class="clara__resizer-grip"></span>
    </div>

    <!--
      Sem faixa escura: o cabeçalho é a MESMA superfície do corpo, separado só
      pela linha. A marca dele some no estado vazio porque quem apresenta a
      Clara ali é o herói logo abaixo — dois wordmarks a 80px um do outro é
      repetição, não identidade.
    -->
    <header
      class="clara__head"
      :class="{ 'clara__head--vazio': !hasMessages }"
    >
      <div
        v-if="hasMessages"
        class="clara__brand"
      >
        <ClaraLogo
          class="clara__head-logo"
          :variant="logoVariant"
        />
        <span class="clara__subtitle pt-1">Inteligência Artificial</span>
      </div>

      <div class="clara__head-actions">
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              class="clara__icon-btn"
              data-test="ai-history"
              aria-label="Conversas anteriores"
              v-bind="attrs"
              v-on="on"
              @click="toggleHistory"
            >
              <PhClockCounterClockwise :size="16" />
            </v-btn>
          </template>
          <span>Conversas anteriores</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              class="clara__icon-btn"
              data-test="ai-new-chat"
              aria-label="Nova conversa"
              v-bind="attrs"
              v-on="on"
              @click="clearChat"
            >
              <PhNoteBlank :size="16" />
            </v-btn>
          </template>
          <span>Limpa a conversa e recomeça</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              class="clara__icon-btn"
              aria-label="Fechar a Clara"
              v-bind="attrs"
              v-on="on"
              @click="close"
            >
              <PhX :size="16" />
            </v-btn>
          </template>
          <span>Fechar</span>
        </v-tooltip>
      </div>
    </header>

    <ClaraContextBar
      :customer="context.customer"
      :operation="context.operation"
      @clear="clearContext"
      @change="openCustomerPicker(null)"
    />

    <div
      ref="messagesContainer"
      class="clara__body"
    >
      <!-- Historico NO LUGAR da conversa, nao por cima: um overlay taparia o
           que o operador ainda esta lendo, e aqui a volta e um clique. -->
      <div
        v-if="showHistory"
        class="clara__history"
        data-test="ai-history-list"
      >
        <div class="clara__history-head">
          <span class="clara__history-title">Conversas anteriores</span>
          <button
            type="button"
            class="clara__history-back"
            data-test="ai-history-back"
            @click="showHistory = false"
          >
            Voltar
          </button>
        </div>

        <p
          v-if="historyLoading"
          class="clara__history-empty"
        >
          Carregando...
        </p>
        <p
          v-else-if="historyError"
          class="clara__history-empty"
        >
          Não foi possível carregar o histórico. Tente novamente.
        </p>
        <p
          v-else-if="!historyItems.length"
          class="clara__history-empty"
        >
          Nenhuma conversa anterior por aqui. As próximas conversas aparecem
          nesta lista automaticamente.
        </p>

        <button
          v-for="item in historyItems"
          :key="item.sessionId"
          type="button"
          class="clara__history-item"
          data-test="ai-history-item"
          @click="openConversation(item)"
        >
          <span class="clara__history-item-title">{{ item.titulo }}</span>
          <span class="clara__history-item-meta">
            <template v-if="item.clienteNome">
              <PhUser :size="12" />
              <span class="clara__history-item-subject">{{ item.clienteNome }}</span>
              <span class="clara__history-item-dot">·</span>
            </template>
            <template v-else-if="item.operacaoId">
              <PhCurrencyDollar :size="12" />
              <span class="clara__history-item-subject">Operação #{{ item.operacaoId }}</span>
              <span class="clara__history-item-dot">·</span>
            </template>
            {{ formatHistoryDate(item.ultimaMensagemEm) }}
          </span>
        </button>
      </div>

      <template v-else>
        <div
          v-if="!hasMessages"
          class="clara__empty"
        >
          <!-- Sangra de borda a borda: os fios são o FUNDO da tela ociosa, não um
             cartão dentro dela. Por isso o herói cancela o respiro do corpo, e
             o respiro horizontal desce para o conteúdo abaixo. -->
          <div class="clara__hero">
            <ClaraStrands
              class="clara__hero-fios"
              tone="light"
              :blur="4"
            />
            <ClaraLogo
              class="clara__hero-logo"
              :variant="logoVariant"
            />
          </div>

          <div class="clara__empty-corpo">
            <p class="clara__empty-title">Como posso ajudar?</p>
            <p class="clara__empty-text">
              Tire dúvidas sobre o sistema, analise dados de clientes e resolva pendências.
            </p>

            <div class="clara__suggestions">
              <button
                v-for="suggestion in suggestions"
                :key="suggestion.label"
                type="button"
                class="clara__suggestion"
                data-test="ai-suggestion-chip"
                @click="handleSuggestionClick(suggestion)"
              >
                {{ suggestion.label }}
              </button>
            </div>

            <div
              v-if="lastCustomer"
              class="clara__last"
              data-test="ai-last-customer"
            >
              <span class="clara__last-label">Último cliente</span>
              <button
                type="button"
                class="clara__suggestion clara__suggestion--accent"
                @click="resumeLastCustomer"
              >
                <PhUser :size="12" />
                {{ lastCustomer.nome }}
              </button>
            </div>
          </div>
        </div>

        <template v-for="msg in messages">
          <ClaraCustomerPicker
            v-if="msg.type === PICKER_TYPE"
            :key="msg.id"
            @select="handleCustomerPicked(msg, $event)"
            @cancel="dismissCustomerPicker(msg)"
          />
          <ClaraOperationPicker
            v-else-if="msg.type === OPERATION_PICKER_TYPE"
            :key="msg.id"
            :customer="msg.customer"
            @select="handleOperationPicked(msg, $event)"
            @cancel="dismissPicker(msg)"
          />
          <!-- Enquanto o primeiro token nao chega o balao estaria VAZIO. Quem
             informa que a Clara esta trabalhando e a linha de status logo
             abaixo, que ainda diz o que ela esta fazendo ("Analisando...",
             "Consultando dados..."). Um balao vazio ao lado dela nao acrescenta
             e ainda salta na tela quando o texto comeca a preencher. -->
          <ChatMessage
            v-else-if="!estaPensando(msg)"
            :key="msg.id"
            :message="msg"
            @retry="handleRetry(msg)"
            @action="handleQuickAction"
          />
        </template>

        <div
          v-if="isStreaming"
          class="clara__status"
        >
          <ChatTypingIndicator />
          <span class="clara__status-text">{{ streamingStatusText }}</span>
        </div>
      </template>
    </div>

    <div class="clara__foot">
      <!-- Aurora so no estado VAZIO: ela e o convite da tela ociosa. Assim que a
           conversa comeca, a leitura passa a ser o texto das mensagens, e uma
           mancha de cor subindo atras da ultima linha so atrapalha. Sai junto
           com o estado vazio, pela mesma condicao. -->
      <ClaraAurora
        v-if="!hasMessages"
        class="clara__aurora"
      />
      <ChatInput
        :disabled="isStreaming"
        :is-streaming="isStreaming"
        @send="handleSend"
        @stop="handleStop"
      />
    </div>

    <p class="clara__disclaimer">
      Gerado por IA a partir da base de conhecimento. Confirme informações críticas com o suporte.
    </p>
  </aside>
</template>

<script>
// Components
import ChatMessage from '@/views/ai-assistant/components/ChatMessage.vue';
import ChatInput from '@/views/ai-assistant/components/ChatInput.vue';
import ChatTypingIndicator from '@/views/ai-assistant/components/ChatTypingIndicator.vue';
import ClaraContextBar from '@/views/clara/ClaraContextBar.vue';
import ClaraLogo from '@/views/clara/ClaraLogo.vue';
import ClaraStrands from '@/views/clara/ClaraStrands.vue';
import ClaraAurora from '@/views/clara/ClaraAurora.vue';
import ClaraCustomerPicker from '@/views/clara/ClaraCustomerPicker.vue';
import ClaraOperationPicker from '@/views/clara/ClaraOperationPicker.vue';

// API
import aiAssistantApi from '@/api/ai-assistant/aiAssistantApi';

// Utils
import utilsStorage from '@/utils/storage';
import { OPTIONS_STORE_CLARA } from '@/vuex/module/clara';

/**
 * Sugestões da tela vazia.
 *
 * `requiresCustomer` marca as que não fazem sentido sem um cliente — elas abrem
 * o seletor antes de enviar. Antes disso as sugestões iam ao pé da letra e a
 * Clara recebia "cliente X", saindo em busca de um cliente chamado X.
 *
 * `{cliente}` no template é trocado pelo nome escolhido.
 */
const ASK = (label, template, flags = {}) => ({
  label,
  template: template || label,
  requiresCustomer: Boolean(flags.requiresCustomer),
  picksOperation: Boolean(flags.picksOperation)
});

const SUGGESTIONS_BY_PROFILE = {
  1: [
    ASK('O que falta pro cliente operar?', 'O que falta pro cliente {cliente} operar?', { requiresCustomer: true }),
    ASK('Qual o limite do cliente?', 'Qual o limite do cliente {cliente}?', { requiresCustomer: true }),
    ASK('Por que o receiver foi rejeitado?', 'Por que o receiver do cliente {cliente} foi rejeitado?', { requiresCustomer: true }),
    ASK('Como funciona o DolsPay?')
  ],
  2: [
    ASK('O que falta pro cliente operar?', 'O que falta pro cliente {cliente} operar?', { requiresCustomer: true }),
    ASK('Como crio uma operação stablecoin?'),
    ASK('Por que o envio ao exterior não foi concluído?'),
    ASK('Como reenvio um e-mail pro cliente?')
  ],
  3: [
    ASK('Como acompanho minha operação?'),
    ASK('O pagamento foi confirmado?'),
    ASK('Como gero o recibo SWIFT?'),
    ASK('Quanto tempo demora o envio?')
  ]
};

const DEFAULT_SUGGESTIONS = [
  ASK('Como criar uma operação?'),
  ASK('O que falta pro cliente operar?', 'O que falta pro cliente {cliente} operar?', { requiresCustomer: true }),
  ASK('Como reenvio um e-mail?'),
  ASK('Como funciona o DolsPay?')
];

/* Com contexto na barra as sugestoes deixam de ser genericas: elas passam a
   perguntar sobre QUEM ja esta na conversa. Sem isso o operador que abriu a
   Clara pelo cadastro de um cliente via "Como reenvio um e-mail?" na frente. */
const SUGGESTIONS_CUSTOMER = [
  ASK('Qual status da operação?', null, { picksOperation: true }),
  ASK('Quais limites disponíveis?', 'Quais os limites disponíveis do cliente {cliente}?'),
  ASK('O que falta para o cliente operar?', 'O que falta para o cliente {cliente} operar?'),
  ASK('Qual foi a rejeição do banco?', 'Qual foi a pendência ou rejeição do banco para o cliente {cliente}?')
];

const SUGGESTIONS_OPERATION = [
  ASK('Qual meu lucro?', 'Qual o lucro da operação {operacao}?'),
  ASK('Detalhes da operação?', 'Detalhes da operação {operacao}'),
  ASK('Qual status da operação?', 'Qual o status da operação {operacao}?')
];

const CUSTOMER_TOKEN = '{cliente}';
const OPERATION_TOKEN = '{operacao}';
const PICKER_TYPE = 'customer-picker';
const OPERATION_PICKER_TYPE = 'operation-picker';

/* Seletores sao widgets pendentes, nao conversa: nao vao para o historico do
   modelo nem para o localStorage. */
const isPicker = (m) => m.type === PICKER_TYPE || m.type === OPERATION_PICKER_TYPE;

const TOOL_STATUS_LABEL = {
  searchCustomers: 'Buscando clientes...',
  getCustomerInfo: 'Carregando dados do cliente...',
  getCustomerOperations: 'Consultando operações...',
  getOperationDetail: 'Carregando detalhes da operação...',
  getStablecoinStatus: 'Verificando status DolsPay...',
  getIntegrationLogs: 'Consultando logs de integração...',
  checkCustomerReadiness: 'Verificando prontidão do cliente...',
  getCustomerLimits: 'Consultando limites do cliente...',
  getReceiverDiagnostic: 'Verificando cadastro DolsPay...',
  getOnboardingTimeline: 'Carregando timeline de onboarding...',
  getOperationBlockReason: 'Identificando bloqueios...'
};

const LAST_CUSTOMER_KEY = 'ai-assistant-last-customer';
const MESSAGES_KEY = 'ai-assistant-messages';
const CONTEXT_KEY = 'ai-assistant-context';
const SESSION_KEY = 'ai-assistant-session';

/* Id do FIO da conversa: e ele que agrupa os turnos no historico do servidor.
   Gerado aqui porque a conversa nasce no painel, nao no backend. */
const novoSessionId = () =>
  (window.crypto?.randomUUID && window.crypto.randomUUID())
  || `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export default {
  name: 'ClaraPanel',
  components: {
    ChatMessage, ChatInput, ChatTypingIndicator,
    ClaraContextBar, ClaraCustomerPicker, ClaraOperationPicker,
    ClaraLogo, ClaraStrands, ClaraAurora
  },

  data() {
    return {
      PICKER_TYPE,
      OPERATION_PICKER_TYPE,
      messages: [],
      sessionId: null,
      showHistory: false,
      historyLoading: false,
      historyError: false,
      historyItems: [],
      isStreaming: false,
      activeToolName: null,
      lastCustomer: null,
      /* De QUEM a conversa trata. Vai junto de cada mensagem para o servidor —
         não é lido do texto. Tipado desde já para operações entrarem depois. */
      context: { customer: null, operation: null },
      abortController: null,
      isResizing: false
    };
  },

  computed: {
    /* O wordmark agora vive sobre a superfície do tema, não mais sobre a faixa
       escura: no claro vale o gradiente da marca, no escuro a variante reversa,
       que é a única que passa de 3:1 ali. */
    logoVariant() {
      return this.$vuetify.theme.dark ? 'reverse' : 'brand';
    },

    isOpen() {
      return this.$store.getters[OPTIONS_STORE_CLARA.GETTERS.IS_OPEN];
    },
    conversationRequest() {
      return this.$store.getters[OPTIONS_STORE_CLARA.GETTERS.REQUEST];
    },
    width() {
      return this.$store.getters[OPTIONS_STORE_CLARA.GETTERS.WIDTH];
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    panelStyle() {
      return this.isMobile ? {} : { width: `${this.width}px` };
    },
    hasMessages() {
      return this.messages.length > 0;
    },
    suggestions() {
      /* Operacao e o contexto mais especifico: quando ha as duas coisas, quem
         manda e ela. */
      if (this.context.operation) return SUGGESTIONS_OPERATION;

      if (this.context.customer) return SUGGESTIONS_CUSTOMER;

      const user = utilsStorage.getUserOnStorage();
      const profileId = Number(user?.profileId || user?.profile?.id);

      return SUGGESTIONS_BY_PROFILE[profileId] || DEFAULT_SUGGESTIONS;
    },
    conversationHistory() {
      return this.messages
        .filter((m) => !m.isError && !isPicker(m))
        .map((m) => ({ role: m.role, content: m.content }));
    },
    streamingStatusText() {
      if (this.activeToolName) {
        return TOOL_STATUS_LABEL[this.activeToolName] || 'Consultando dados...';
      }

      return 'Analisando...';
    }
  },

  watch: {
    /* Pedido vindo de outra tela ("conversar sobre este cliente"). Encerra a
       conversa atual e comeca uma nova ja com o contexto: continuar a anterior
       misturaria dois assuntos, e o operador clicou justamente para falar de
       outra coisa. */
    conversationRequest: {
      handler(request) {
        if (!request || !request.seq) return;

        if (!request.customer && !request.operation) return;

        this.clearChat();

        if (request.customer) this.setContextCustomer(request.customer);

        if (request.operation) this.setContextOperation(request.operation);

        this.$store.commit(OPTIONS_STORE_CLARA.MUTATIONS.CLEAR_REQUEST);
        this.scrollToBottom();
      },
      deep: true
    },

    messages: {
      handler(newMessages) {
        if (!newMessages.length) return localStorage.removeItem(MESSAGES_KEY);

        // O seletor é um widget pendente, não conversa: restaurá-lo traria de
        // volta uma pergunta que o usuário já abandonou.
        const toSave = newMessages
          .filter((m) => !m.isStreaming && !isPicker(m))
          .slice(-20);

        localStorage.setItem(MESSAGES_KEY, JSON.stringify(toSave));
      },
      deep: true
    },

    isOpen(open) {
      if (open) this.scrollToBottom();
    }
  },

  mounted() {
    this.restoreMessages();

    // So vale restaurar o fio se sobrou conversa: fio sem mensagens criaria
    // continuidade fantasma com uma conversa que o operador ja descartou.
    if (this.messages.length) this.sessionId = localStorage.getItem(SESSION_KEY) || null;

    this.lastCustomer = this._loadLastCustomer();
    this.restoreContext();
  },

  beforeDestroy() {
    this._pararResize();
  },

  methods: {
    close() {
      this.$store.commit(OPTIONS_STORE_CLARA.MUTATIONS.SET_OPEN, false);
    },

    // --- redimensionamento ---------------------------------------------------
    startResize() {
      this.isResizing = true;
      // Os listeners vão na window, não na alça: o ponteiro sai dela assim que
      // o arraste começa, e no document o gesto continua funcionando.
      window.addEventListener('mousemove', this._aoArrastar);
      window.addEventListener('mouseup', this._pararResize);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },

    _aoArrastar(evento) {
      // O painel está ancorado à direita, então a largura é o que sobra dali
      // até a borda da janela.
      this.$store.commit(
        OPTIONS_STORE_CLARA.MUTATIONS.SET_WIDTH,
        window.innerWidth - evento.clientX
      );
    },

    _pararResize() {
      if (!this.isResizing) return;

      this.isResizing = false;
      window.removeEventListener('mousemove', this._aoArrastar);
      window.removeEventListener('mouseup', this._pararResize);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    },

    adjustWidth(delta) {
      this.$store.commit(OPTIONS_STORE_CLARA.MUTATIONS.SET_WIDTH, this.width + delta);
    },

    // --- conversa ------------------------------------------------------------
    restoreMessages() {
      try {
        const saved = localStorage.getItem(MESSAGES_KEY);

        if (saved) this.messages = JSON.parse(saved);
      } catch (e) {
        // Histórico corrompido não deve impedir o uso: começa vazio.
      }
    },

    _loadLastCustomer() {
      try {
        const saved = sessionStorage.getItem(LAST_CUSTOMER_KEY);

        return saved ? JSON.parse(saved) : null;
      } catch (e) {
        return null;
      }
    },

    _saveLastCustomer(customer) {
      if (!customer?.id || !customer?.nome) return;

      try {
        sessionStorage.setItem(LAST_CUSTOMER_KEY, JSON.stringify(customer));
        this.lastCustomer = customer;
      } catch (e) {
        // Sem sessionStorage o atalho apenas não aparece na próxima abertura.
      }

      // Toda tool que resolve um cliente move o contexto junto. É o que faz a
      // barra acompanhar a conversa sem o operador precisar fixar nada à mão.
      this.setContextCustomer({ id: customer.id, name: customer.nome });
    },

    // --- contexto -------------------------------------------------------------
    restoreContext() {
      /* Contexto órfão: se não sobrou conversa, o cliente de dias atrás não faz
         sentido — a barra abriria anunciando alguém que o operador esqueceu. */
      if (!this.messages.length) return this.clearContext();

      try {
        const saved = localStorage.getItem(CONTEXT_KEY);

        if (saved) this.context = { customer: null, operation: null, ...JSON.parse(saved) };
      } catch (e) {
        // Contexto corrompido: segue sem ele, a conversa não depende disso.
      }
    },

    persistContext() {
      try {
        if (!this.context.customer && !this.context.operation) {
          localStorage.removeItem(CONTEXT_KEY);

          return;
        }

        localStorage.setItem(CONTEXT_KEY, JSON.stringify(this.context));
      } catch (e) {
        // Sem localStorage o contexto só não sobrevive ao reload.
      }
    },

    setContextCustomer(customer) {
      if (!customer?.id) return;

      this.context = { ...this.context, customer: { id: customer.id, name: customer.name } };
      this.persistContext();
    },

    setContextOperation(operation) {
      if (!operation?.id) return;

      /* A operacao carrega o cliente dela: sem isso, trocar de operacao
         deixaria na barra o cliente da anterior. */
      this.context = {
        customer: operation.customer || this.context.customer,
        operation: { id: operation.id }
      };
      this.persistContext();
    },

    clearContext() {
      this.context = { customer: null, operation: null };
      this.persistContext();
    },

    clearChat() {
      this.messages = [];
      localStorage.removeItem(MESSAGES_KEY);
      this.clearContext();
      this._setSessionId(null);
      this.showHistory = false;
    },

    _setSessionId(sessionId) {
      this.sessionId = sessionId;

      try {
        if (sessionId) localStorage.setItem(SESSION_KEY, sessionId);
        else localStorage.removeItem(SESSION_KEY);
      } catch (e) {
        // Sem localStorage o fio so nao sobrevive ao reload.
      }
    },

    // --- historico de conversas ----------------------------------------------
    async toggleHistory() {
      if (this.showHistory) {
        this.showHistory = false;

        return;
      }

      this.showHistory = true;
      this.historyLoading = true;
      this.historyError = false;

      try {
        this.historyItems = await aiAssistantApi.listConversations();
      } catch (e) {
        this.historyError = true;
        this.historyItems = [];
      } finally {
        this.historyLoading = false;
      }
    },

    /* Reabrir e CONTINUAR: os turnos viram mensagens normais e o fio antigo
       volta a ser o fio atual — a proxima pergunta entra na mesma conversa,
       com o historico textual indo ao modelo como sempre. */
    async openConversation(item) {
      this.historyLoading = true;

      try {
        const turns = await aiAssistantApi.getConversation(item.sessionId);
        const messages = [];

        turns.forEach((turn) => {
          messages.push({
            id: `hist-u-${turn.id}`,
            role: 'user',
            content: turn.pergunta,
            timestamp: turn.em,
          });

          if (turn.resposta) {
            messages.push({
              id: `hist-a-${turn.id}`,
              role: 'assistant',
              content: turn.resposta,
              sources: [],
              actions: [],
              conversationId: turn.id,
              timestamp: turn.em,
              isStreaming: false,
              isError: turn.status === 'error',
            });
          }
        });

        this.messages = messages;
        this._setSessionId(item.sessionId);
        /* A barra de contexto nao viaja no historico: melhor vazia do que
           anunciando um cliente que talvez nem seja o da conversa reaberta. */
        this.clearContext();
        this.showHistory = false;
        this.scrollToBottom();
      } catch (e) {
        this.historyError = true;
      } finally {
        this.historyLoading = false;
      }
    },

    formatHistoryDate(iso) {
      if (!iso) return '';

      const data = new Date(iso);

      if (Number.isNaN(data.getTime())) return '';

      const hora = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

      return data.toDateString() === new Date().toDateString()
        ? `Hoje ${hora}`
        : `${data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${hora}`;
    },

    resumeLastCustomer() {
      if (!this.lastCustomer) return;

      // Retomar o último cliente é fixá-lo: a barra já sobe preenchida em vez
      // de esperar a tool responder para só então aparecer.
      this.setContextCustomer({ id: this.lastCustomer.id, name: this.lastCustomer.nome });
      this.handleSend(`Me mostre os dados do cliente ${this.lastCustomer.nome}`);
    },

    handleSuggestionClick(suggestion) {
      /* Quem já tem cliente na barra não é perguntado de novo: o template é
         preenchido com o contexto e a pergunta sai direto. */
      if (suggestion.picksOperation) {
        this.openOperationPicker();

        return;
      }

      if (suggestion.requiresCustomer && !this.context.customer) {
        this.openCustomerPicker(suggestion.template);

        return;
      }

      this.handleSend(this.fillTemplate(suggestion.template));
    },

    fillTemplate(template) {
      const name = this.context.customer?.name;
      const operationId = this.context.operation?.id;

      let texto = name
        ? template.replace(CUSTOMER_TOKEN, name)
        : template.replace(CUSTOMER_TOKEN, '');

      texto = operationId
        ? texto.replace(OPERATION_TOKEN, `#${operationId}`)
        : texto.replace(OPERATION_TOKEN, '');

      return texto.replace(/\s{2,}/g, ' ').trim();
    },

    // --- seletor de cliente ---------------------------------------------------
    /* "Qual status da operação?" no contexto de cliente nao vira pergunta: vira
       lista. O operador escolhe a operacao, ela entra no contexto e so entao a
       pergunta sai — senao a Clara receberia "a operacao" sem saber qual. */
    openOperationPicker() {
      if (!this.context.customer) {
        this.openCustomerPicker(null);

        return;
      }

      this.messages = this.messages.filter(
        (m) => !isPicker(m)
      );

      this.messages.push({
        id: `op-picker-${Date.now()}`,
        type: OPERATION_PICKER_TYPE,
        customer: this.context.customer
      });

      this.scrollToBottom();
    },

    handleOperationPicked(msg, operation) {
      this.dismissPicker(msg);
      this.setContextOperation(operation);
      this.handleSend(this.fillTemplate('Qual o status da operação {operacao}?'));
    },

    dismissPicker(msg) {
      this.messages = this.messages.filter((m) => m.id !== msg.id);
    },

    openCustomerPicker(pendingTemplate) {
      // Um seletor de cada vez: dois widgets abertos deixariam ambíguo qual
      // pergunta a escolha responde.
      this.messages = this.messages.filter((m) => !isPicker(m));

      this.messages.push({
        id: `picker-${Date.now()}`,
        type: PICKER_TYPE,
        pendingTemplate: pendingTemplate || null
      });

      this.scrollToBottom();
    },

    dismissCustomerPicker(msg) {
      this.messages = this.messages.filter((m) => m.id !== msg.id);
    },

    handleCustomerPicked(msg, customer) {
      const template = msg.pendingTemplate;

      this.dismissCustomerPicker(msg);
      this.setContextCustomer(customer);

      // Sem template a escolha veio da barra: o operador só trocou de cliente e
      // não fez pergunta nenhuma.
      if (template) this.handleSend(this.fillTemplate(template));
    },

    // --- ações rápidas --------------------------------------------------------
    /* Nenhuma ação executa nada: cada botão só LEVA para a tela certa já com o
       cliente aplicado — consultar o cadastro, abrir o modal de adicionar
       banco na aba de bancos e limites, ou montar a operação. */
    handleQuickAction(action) {
      const route = this._quickActionRoute(action);

      if (!route) return;

      /* O FormCustomer carrega o cliente só no mount: trocar o :id com a tela
         já aberta deixaria os dados do cliente anterior na frente. Só nesse
         caso a navegação vira reload completo. */
      const current = this.$route;
      const sameFormOtherCustomer =
        route.name === 'FormEditCustomer' &&
        current?.name === 'FormEditCustomer' &&
        String(current.params?.id) !== String(route.params.id);

      if (sameFormOtherCustomer) {
        window.location.assign(this.$router.resolve(route).href);

        return;
      }

      this.$router.push(route).catch(() => {});

      // No desktop o painel convive com a tela; no mobile ele a cobre inteira.
      if (this.isMobile) this.close();
    },

    _quickActionRoute(action) {
      /* A operação se resolve ANTES do cliente: ela é o único destino que não
         depende de customerId, e o guard abaixo a descartaria. */
      if (action?.type === 'view-operation') {
        return action.operationId
          ? { name: 'OperationDetail', params: { id: String(action.operationId) } }
          : null;
      }

      const customerId = action?.customerId;

      if (!customerId) return null;

      switch (action.type) {
        case 'add-bank':
          /* `addBank` é consumido pela aba de bancos e limites, que abre o
             mesmo modal de Adicionar banco do kanban de onboarding. */
          return {
            name: 'FormEditCustomer',
            params: { id: customerId },
            query: { tab: 'limits', addBank: '1' }
          };

        case 'view-customer':
          return {
            name: 'FormEditCustomer',
            params: { id: customerId },
            query: action.tab ? { tab: action.tab } : {}
          };

        case 'create-operation':
          if (!action.idExchangeType) return null;

          return {
            name: 'FormOperation',
            query: {
              idExchangeType: String(action.idExchangeType),
              idCustomer: String(customerId),
              ...(action.idBank ? { idBank: String(action.idBank) } : {})
            }
          };

        default:
          return null;
      }
    },

    handleRetry(msg) {
      const userMsgIndex = this.messages.findIndex((m) => m.id === msg.relatedUserMsgId);

      if (userMsgIndex === -1) return;

      const userMessage = this.messages[userMsgIndex].content;

      this.messages.splice(userMsgIndex, 2);
      this.handleSend(userMessage);
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;

        if (container) container.scrollTop = container.scrollHeight;
      });
    },

    async handleSend(message) {
      if (!this.sessionId) this._setSessionId(novoSessionId());

      this.showHistory = false;

      const userMsg = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: message,
        timestamp: new Date()
      };

      this.messages.push(userMsg);

      const assistantMsg = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: '',
        sources: [],
        /* Declarado desde já: propriedade criada depois não seria reativa e o
           balão não re-renderizaria quando as ações chegassem. */
        actions: [],
        timestamp: new Date(),
        isStreaming: true,
        isError: false,
        relatedUserMsgId: userMsg.id
      };

      this.messages.push(assistantMsg);
      this.isStreaming = true;
      this.activeToolName = null;
      this.abortController = new AbortController();

      this.scrollToBottom();

      try {
        await aiAssistantApi.streamChat(
          {
            message,
            conversationHistory: this.conversationHistory.slice(0, -2),
            context: this.context,
            sessionId: this.sessionId
          },
          {
            onToken: (token) => {
              // Chegou fala: a consulta acabou, o status "Consultando..." sai.
              if (this.activeToolName) this.activeToolName = null;

              assistantMsg.content += token;
              this.$forceUpdate();
              this.scrollToBottom();
            },
            onSources: (sources, conversationId) => {
              assistantMsg.sources = sources;
              assistantMsg.conversationId = conversationId || null;
            },
            onToolCall: (toolName) => {
              this.activeToolName = toolName;

              /* Texto emitido ANTES de uma consulta é narração de processo
                 ("vou verificar...", "a busca retornou errado") — a resposta
                 de verdade é o que vem depois da ÚLTIMA consulta. Descartar
                 aqui poupa o operador da mecânica interna e ainda limpa o
                 histórico que volta ao modelo no turno seguinte. */
              if (assistantMsg.content) {
                assistantMsg.content = '';
                this.$forceUpdate();
              }
            },
            onLastCustomer: (customer) => {
              this._saveLastCustomer(customer);
            },
            // O servidor recusou o contexto (id fora da permissão, cliente
            // removido). Limpar aqui evita a barra anunciar o que já não vale.
            onContextChange: (resolved) => {
              if (!resolved) this.clearContext();
            },
            // Ações rápidas sugeridas pelo servidor — viram botões no balão.
            onActions: (actions) => {
              assistantMsg.actions = actions || [];
            },
            onError: (errorMsg) => {
              assistantMsg.content = errorMsg || 'Não foi possível processar sua pergunta. Tente novamente em alguns instantes; se continuar, fale com o suporte.';
              assistantMsg.isError = true;
              this.$forceUpdate();
            },
            onDone: () => {
              assistantMsg.isStreaming = false;
              this.isStreaming = false;
              this.activeToolName = null;
              this.abortController = null;
              this.$forceUpdate();
              this.scrollToBottom();
            },
            abortController: this.abortController
          }
        );
      } catch (error) {
        if (error?.name === 'AbortError') {
          assistantMsg.content += '\n\n_(Resposta interrompida)_';
        } else {
          assistantMsg.content =
            'Não foi possível conectar à Clara. Verifique sua conexão e tente novamente.';
          assistantMsg.isError = true;
        }

        assistantMsg.isStreaming = false;
        this.isStreaming = false;
        this.activeToolName = null;
        this.abortController = null;
        this.$forceUpdate();
      }
    },

    /* Mensagem da Clara que ainda nao recebeu nada. Erro e interrupcao nao
       entram aqui: os dois preenchem `content` antes de encerrar o streaming. */
    estaPensando(msg) {
      return msg.role === 'assistant' && msg.isStreaming && !msg.content;
    },

    handleStop() {
      if (this.abortController) this.abortController.abort();
    }
  }
};
</script>

<style scoped>
.clara {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 7;
  display: flex;
  flex-direction: column;
  background: var(--tc-surface);
  border-left: 1px solid var(--tc-line);
}

.clara--mobile {
  width: 100%;
  z-index: 210;
}

/* Durante o arraste a transição sai do caminho: com ela, a largura persegue o
   ponteiro com atraso e o gesto fica elástico. */
.clara:not(.clara--resizing) {
  transition: width 0.18s ease;
}

/* --- alça ----------------------------------------------------------------- */
.clara__resizer {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -3px;
  width: 7px;
  cursor: col-resize;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.clara__resizer-grip {
  width: 2px;
  height: 34px;
  border-radius: 2px;
  background: var(--tc-line);
  transition: background var(--tc-transition), height var(--tc-transition);
}

.clara__resizer:hover .clara__resizer-grip,
.clara__resizer:focus-visible .clara__resizer-grip,
.clara--resizing .clara__resizer-grip {
  background: var(--clara-brand);
  height: 56px;
}

/* --- cabeçalho ------------------------------------------------------------ */
/* Mesma superfície do corpo. A faixa escura saiu: ela vinha de quando o painel
   precisava se anunciar, e o preço era um bloco de 64px de cor forte no topo de
   toda tela, competindo com o conteúdo da conversa logo abaixo. Agora quem
   separa é a linha, e a cor da Clara fica para o que é dela — os fios do herói,
   o balão e os estados de ação. */
.clara__head {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--tc-space-3);
  min-height: 56px;
  padding: var(--tc-space-3) var(--tc-space-4);
  background: var(--tc-surface);
  border-bottom: 1px solid var(--tc-line);
}

/* Vazio, o cabeçalho é só o porta-ações: sem marca e sem linha, para o herói
   começar na primeira aresta do painel. */
.clara__head--vazio {
  justify-content: flex-end;
  border-bottom-color: transparent;
}

.clara__brand {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  position: relative;
  z-index: 1;
}

/* A marca ocupa o lugar do antigo selo + título em texto: o wordmark já diz
   o nome, então repetir "Clara AI" ao lado seria redundância. */
.clara__head-logo {
  width: 92px;
}

.clara__subtitle {
  font-family: var(--tc-font-mono);
  font-size: 10px;
  letter-spacing: var(--tc-tracking-label);
  text-transform: uppercase;
  color: var(--clara-text);
}

.clara__head-actions {
  display: flex;
  align-items: center;
  gap: var(--tc-space-1);
  position: relative;
  z-index: 1;
}

/* Ícones em texto secundário, e não mais em branco: o cabeçalho deixou de ser
   escuro, e o branco sumiria nele. */
.clara__icon-btn {
  width: 32px !important;
  height: 32px !important;
  color: var(--tc-text-2) !important;
}

.clara__icon-btn:hover {
  background: var(--clara-tint) !important;
  color: var(--clara-text) !important;
}

/* --- corpo ---------------------------------------------------------------- */
.clara__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: var(--tc-space-4);
}

.clara__status {
  display: flex;
  align-items: center;
  gap: var(--tc-space-2);
  padding: var(--tc-space-2) var(--tc-space-3);
}

.clara__status-text {
  font-family: var(--tc-font-mono);
  font-size: var(--tc-fs-label);
  letter-spacing: 0.06em;
  color: var(--tc-text-3);
}

/* --- estado vazio --------------------------------------------------------- */
/* A margem negativa cancela o respiro do .clara__body — é o que deixa o herói
   ir de aresta a aresta. Só o respiro HORIZONTAL desce para o conteúdo abaixo;
   o de baixo continua no .clara__empty. */
.clara__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin: calc(var(--tc-space-4) * -1) calc(var(--tc-space-4) * -1) 0;
  padding-bottom: var(--tc-space-7);
}

/* O herói: os fios de lado a lado, o wordmark centralizado por cima. Ele é a
   apresentação da Clara na tela ociosa — o papel que a faixa escura do
   cabeçalho fazia, agora sem cortar o painel em dois. */
.clara__hero {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: var(--tc-space-7) var(--tc-space-5);
  margin-bottom: var(--tc-space-5);
  overflow: hidden;
}

.clara__hero-fios {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

/* `position: relative` sem z-index: basta sair do fluxo estático para ficar
   acima dos fios, que são o irmão anterior. */
.clara__hero-logo {
  position: relative;
  width: 148px;
  max-width: 60%;
}

.clara__empty-corpo {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 var(--tc-space-4);
}

.clara__empty-title {
  font-family: var(--tc-font-display);
  font-size: var(--tc-fs-h3);
  font-weight: var(--tc-fw-semibold);
  color: var(--tc-text);
  margin: 0 0 var(--tc-space-2);
}

.clara__empty-text {
  font-size: var(--tc-fs-sm);
  color: var(--tc-text-2);
  max-width: 34ch;
  line-height: var(--tc-lh-body);
  margin: 0 0 var(--tc-space-5);
}

.clara__suggestions {
  display: flex;
  flex-direction: column;
  gap: var(--tc-space-2);
  width: 100%;
}

.clara__suggestion {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--tc-space-2);
  width: 100%;
  min-height: 36px;
  padding: var(--tc-space-2) var(--tc-space-3);
  border: 1px solid var(--tc-line);
  border-radius: var(--tc-radius-sm);
  background: var(--tc-surface);
  color: var(--tc-text-2);
  font-family: var(--tc-font-body);
  font-size: var(--tc-fs-sm);
  font-weight: var(--tc-fw-medium);
  cursor: pointer;
  transition: border-color var(--tc-transition), color var(--tc-transition),
    background var(--tc-transition);
}

.clara__suggestion:hover {
  border-color: var(--clara-brand);
  background: var(--clara-tint);
  color: var(--tc-text);
}

.clara__suggestion--accent {
  color: var(--clara-text);
  border-color: var(--clara-brand);
}

.clara__last {
  margin-top: var(--tc-space-5);
  width: 100%;
}

.clara__last-label {
  display: block;
  font-family: var(--tc-font-mono);
  font-size: var(--tc-fs-label);
  letter-spacing: var(--tc-tracking-label);
  text-transform: uppercase;
  color: var(--tc-text-3);
  margin-bottom: var(--tc-space-2);
}


/* --- historico de conversas ------------------------------------------------ */
.clara__history {
  display: flex;
  flex-direction: column;
  gap: var(--tc-space-2);
}

.clara__history-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--tc-space-2);
}

.clara__history-title {
  font-family: var(--tc-font-mono);
  font-size: var(--tc-fs-label);
  letter-spacing: var(--tc-tracking-label);
  text-transform: uppercase;
  color: var(--tc-text-3);
}

.clara__history-back {
  border: none;
  background: none;
  padding: 0;
  font-size: var(--tc-fs-sm);
  font-weight: var(--tc-fw-medium);
  color: var(--clara-text);
  cursor: pointer;
}

.clara__history-empty {
  font-size: var(--tc-fs-sm);
  color: var(--tc-text-2);
  line-height: var(--tc-lh-body);
  margin: var(--tc-space-2) 0 0;
}

/* Mesma familia visual das sugestoes, mas alinhado a esquerda: aqui o texto
   e um titulo de conversa, nao um rotulo de acao. */
.clara__history-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  width: 100%;
  padding: var(--tc-space-2) var(--tc-space-3);
  border: 1px solid var(--tc-line);
  border-radius: var(--tc-radius-sm);
  background: var(--tc-surface);
  text-align: left;
  cursor: pointer;
  transition: border-color var(--tc-transition), background var(--tc-transition);
}

.clara__history-item:hover {
  border-color: var(--clara-brand);
  background: var(--clara-tint);
}

.clara__history-item-title {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--tc-fs-sm);
  font-weight: var(--tc-fw-medium);
  color: var(--tc-text);
}

.clara__history-item-meta {
  display: flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  font-family: var(--tc-font-mono);
  font-size: var(--tc-fs-label);
  color: var(--tc-text-3);
}

/* O NOME e a parte que conta: no aperto, e a data que fica inteira e o nome
   que reticencia — nome cortado ainda identifica, data cortada nao. */
.clara__history-item-subject {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--tc-text-2);
  font-weight: var(--tc-fw-medium);
}

.clara__history-item-dot {
  flex: none;
}

/* --- rodapé --------------------------------------------------------------- */
/* --tc-text-2 e não --tc-text-3: o terciário rende 3,34 sobre o branco, abaixo
   de AA. Aviso de IA é informação que precisa ser lida, não decoração. */
/* `bottom: 100%` prende a aurora à aresta de cima do input — é o que faz ela
   ficar colada nele em vez de rolar junto com as mensagens. Fica FORA do
   .clara__body de propósito: dentro, o scroll a levaria embora. */
.clara__foot {
  position: relative;
  flex: none;
}

.clara__aurora {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  height: 92px;
  pointer-events: none;
}

.clara__disclaimer {
  margin: 0;
  padding: var(--tc-space-2) var(--tc-space-4) var(--tc-space-3);
  border-top: 1px solid var(--tc-line);
  font-size: var(--tc-fs-label);
  line-height: 1.45;
  color: var(--tc-text-2);
  text-align: center;
}

</style>
